<template>
  <div>
    <div class="heading">
      <h3>Add a Patient</h3>
    </div>
    <div class="add-patient-row">
      <button @click="$emit('open-find-patient-modal')" class="primary-outlined-button">
        <span v-show="!patient || clearSelectedPatient"
          class="material-symbols-outlined size-20 icon-find-a-patient">search</span>
        <span v-show="!patient || clearSelectedPatient" class="label-find-a-patient">Patient</span>
        <span v-show="patient && !clearSelectedPatient"> {{ patient?.family + ' ' + patient?.given }}</span>
      </button>
      <Dropdown v-model="selectedRequestingPhysician" :options="requestingPhysician" optionLabel="caregiverName"
        placeholder="Requesting Physician" data-pc-section="trigger" class="custom-placeholder-color"
        @change="validateForm" />
      <div v-if="patientDiagnosisDropdown">
        <Dropdown v-model="selectedDiagnosis" :options="patientDiagnosisDropdown" optionLabel="description"
          placeholder="Diagnosis (optional)" class="custom-placeholder-color" @change="validateForm" />
      </div>
      <div v-else>
        <Dropdown v-model="selectedNoDiagnosisFound" :options="noDiagnosisFoundDropdown" optionLabel="description"
          placeholder="Diagnosis (optional)" class="custom-placeholder-color" @change="validateForm" />
      </div>
      <Dropdown v-model="selectedTumorBoard" :options="tumorBoardsDropdown" optionLabel="tb_name"
        placeholder="Tumor Board" class="custom-placeholder-color" @change="validateForm" />
      <Dropdown v-model="priority" :options="priorityOptions" optionLabel="priority" placeholder="Priority"
        class="custom-placeholder-color" @change="validateForm" />
      <Calendar v-model="patientScheduledDate" :minDate="minDate" placeholder="Date (optional)" :manualInput="false"
        dateFormat="dd-M-yy">
        <template #date="slotProps">
          <strong v-if="isHighlightedDatePatientToBeScheduled(slotProps.date)" style="color: var(--Primary)">
            {{ slotProps.date.day }}
          </strong>
          <template v-else>
            {{ slotProps.date.day }}
          </template>
        </template>
      </Calendar>
      <InputText type="text" v-model="notes" class="input-text" placeholder="Request Note (optional)" />
      <button @click="handleAddPatientDetails()" class="primary-filled-button" :disabled="!isDisabled"><span
          class="material-symbols-outlined size-20 icon-find-a-patient">add</span><span
          class="label-find-a-patient">patient</span>
      </button>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { computed, defineEmits, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Dropdown from 'primevue/dropdown'
import InputText from 'primevue/inputtext'
import { getTumorBoards } from '@/ux3/cards/TumorBoardSessions/getTumorBoards'
import { getRequestingPhysicians } from '@/ux3/cards/TumorBoardSideBar/getRequestingPhysicians'
import { getPatientDiagnosis } from '@/ux3/cards/TumorBoardSessions/getPatientDiagnosis'
import { updatePatientSchedule } from '@/ux3/cards/TumorBoardSessions/updatePatientSchedule'
import { getScheduleAPI } from '@/ux3/cards/TumorBoardSideBar/getScheduleAPI'
import { priorityOptions } from '@/common/shared.js'
import Calendar from 'primevue/calendar'
import { getHighlightedDates } from '../TumorBoardSideBar/getHighlightedDates'
import { addDefaultDiagnosis, formatDateForHighlight, formatDateToTBPayload, showToast } from '@/utils/utils'

const store = useStore()
const selectedRequestingPhysician = ref('')
const selectedDiagnosis = ref('')
const selectedNoDiagnosisFound = ref('')
const priority = ref('')
const selectedTumorBoard = ref('')
const tumorBoardsDropdown = ref([])
const notes = ref('')
const requestingPhysician = ref([])
const patientDiagnosisDropdown = ref([])
const noDiagnosisFoundDropdown = ref([{ description: 'Diagnosis Not Assigned', id: 'notFound' }])
const isDisabled = ref(false)
const clearSelectedPatient = ref(false)
const patientScheduledDate = ref('')
const patientToBeScheduledHighlightedDates = ref([])
const minDate = ref(new Date())

const patient = computed(() => store.getters.session.patient)

const emit = defineEmits(['form-change', 'reset-confirmation', 'open-find-patient-modal'])

const validateForm = async () => {
  emit('form-change')
  if (selectedTumorBoard.value?.tb_id) {
    const patientToBeScheduledHighlightedDatesData = await getHighlightedDates(selectedTumorBoard.value.tb_id)
    patientToBeScheduledHighlightedDates.value = patientToBeScheduledHighlightedDatesData
  }
  isDisabled.value = !!(selectedTumorBoard.value?.tb_id && selectedRequestingPhysician.value?.id && patient?.value?.id && priority.value?.priority)
}

const handleAddPatientDetails = async () => {
  const patientDetails = {
    date: patientScheduledDate.value ? formatDateToTBPayload(patientScheduledDate.value) : null,
    tb_id: selectedTumorBoard.value.tb_id,
    schedule: {
      care_giver_id: selectedRequestingPhysician.value.id,
      mrn: patient?.value?.id,
      notes: notes.value,
      priority: priority.value?.priority || '',
      diagnosis: selectedDiagnosis.value?.description || selectedNoDiagnosisFound.value?.description,
      diagnosis_code: selectedDiagnosis.value?.icd10_code,
    },
  }

  const response = await updatePatientSchedule(patientDetails)
  const keyToCheck = 'schedule_ids'
  if (response?.[keyToCheck] !== undefined) {
    if (patientDetails.date === null) {
      showToast('success', 'Patient details added to Patients To Be Scheduled List')
    } else {
      showToast('success', 'Patient details added to Scheduled Patients List')
    }
    isDisabled.value = false
    const schedulePatientFromResponse = await getScheduleAPI()
    store.dispatch('setScheduledAndToBeScheduledPatients', { schedulePatients: schedulePatientFromResponse, publish: true })
    clearSelectedPatient.value = true
    patientScheduledDate.value = ''
  } else if (response?.status) {
    if (response?.message?.includes('yet to be schedule')) {
      showToast('error', 'Patient had already been added')
    } else if (response?.message?.includes('No Meeting')) {
      showToast('error', 'There are no upcoming sessions for this tumor board')
    } else if (response?.status === 401) {
      showToast('error', 'You are not authorized to schedule patients.')
    } else {
      showToast('error', 'Patient has already been scheduled')
    }
    isDisabled.value = false
  } else {
    showToast('error', 'Failed to add Patient details')
  }
  clearPatientInfo()
  emit('reset-confirmation')
}

const clearPatientInfo = () => {
  selectedRequestingPhysician.value = ''
  priority.value = ''
  notes.value = ''
  selectedTumorBoard.value = ''
  selectedDiagnosis.value = ''
  patientScheduledDate.value = ''
  isDisabled.value = false
}

const isHighlightedDatePatientToBeScheduled = (dateObj) => {
  const formattedDate = formatDateForHighlight(dateObj)
  return patientToBeScheduledHighlightedDates.value.dates?.includes(formattedDate)
}

watch(patient, async () => {
  minDate.value = new Date()
  clearSelectedPatient.value = false
  const patientDiagnosisData = await getPatientDiagnosis([patient.value.id])
  patientDiagnosisDropdown.value = addDefaultDiagnosis(patientDiagnosisData)
})

onMounted(async () => {
  clearSelectedPatient.value = true
  const requestingPhysicianFromResponse = await getRequestingPhysicians()
  requestingPhysician.value = requestingPhysicianFromResponse.map(item => ({
    caregiverName: `${item.title} ${item.name}`,
    id: item.care_giver_id,
  }))
  store.dispatch('setRequestingPhysician', { requestingPhysician: requestingPhysicianFromResponse, publish: true })
  const tumorBoardsData = await getTumorBoards()
  tumorBoardsDropdown.value = tumorBoardsData?.tb_types?.map(item => ({ tb_name: item.tb_name, tb_id: item.tb_id }))
})
</script>

<style lang="scss" scoped>
.heading {
  display: flex;
  align-items: flex-start;
}

.add-patient-row {
  container-type: inline-size;

  display: flex;
  flex-direction: row;
  align-items: flex-start;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  gap: 0.5rem;
  margin: 0 12px;
  width: 90%;

  .p-dropdown {
    max-width: 12cqi;
  }
}

.input-text {
  background-color: var(--White);
}

.label-find-a-patient {
  position: relative;
  margin-left: 5px;
}

.icon-find-a-patient {
  position: relative;
}

.heading {
  margin-left: 12px;
}

button {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
}
</style>
